import Server from "../server/Server";
import LexiconEntryMatch from "./LexiconEntryMatch";
import LexiconEntryResult from "./LexiconEntryResult";
import LexiconLanguage from "./LexiconLanguage";
import LexiconLanguages from "./LexiconLanguages";
import LexiconStem from "./LexiconStem";
import LexiconStemMatch from "./LexiconStemMatch";
import LexiconStemResult from "./LexiconStemResult";
import LexiconStore from "./LexiconStore";
import WordPosition from "./WordPosition";

interface RawLexiconStem {
    text: string;
    simple: boolean;
}

interface RawLexiconStemResult {
    stem: string;
    simpleStem: string;
    language: string;
    matches: RawLexiconStem[];
}

interface RawLexiconEntry {
    number: string;
    text: string;
}

interface RawLexiconEntryResult {
    id: string;
    standard: RawLexiconEntry[];
    simple: RawLexiconEntry[];
}

export default class LexiconSearcher {

    public constructor(private readonly lexicons: LexiconStore) {
    }

    public searchStems(text: string, language: LexiconLanguage): Promise<LexiconStemResult>;
    public searchStems(wordPosition: WordPosition): Promise<LexiconStemResult>;
    public searchStems(textOrWordPosition: string | WordPosition, language?: LexiconLanguage): Promise<LexiconStemResult> {
        let path;
        if (typeof textOrWordPosition === 'string') {
            const textEncoded = encodeURIComponent(textOrWordPosition);
            const languageEncoded = encodeURIComponent(language as string);
            path = `lexicon/stems/search/${languageEncoded}/${textEncoded}`;
        } else {
            const pos = textOrWordPosition;
            path = `lexicon/stems/search/${pos.getVerseIndex()}/${pos.getSourceIndex()}/${pos.getWordIndex()}/${pos.getPartIndex()}`;
        }

        return Server.newRequest().get<RawLexiconStemResult>(path).then(result => {
            const resultLanguage = LexiconLanguages.fromString(result.language);
            const stem = new LexiconStem({
                language: resultLanguage,
                text: result.stem
            });
            const simpleStem = new LexiconStem({
                language: resultLanguage,
                text: result.simpleStem
            });
            const matches = result.matches.map(match => new LexiconStemMatch({
                isSimpleMatch: match.simple,
                language: resultLanguage,
                text: match.text
            }));
            return new LexiconStemResult(stem, simpleStem, matches);
        });
    }

    public searchEntries(stem: string, language: LexiconLanguage, strict: boolean): Promise<LexiconEntryResult> {
        const stemEncoded = encodeURIComponent(stem);
        const languageEncoded = encodeURIComponent(language);
        const strictPath = strict ? '/strict' : '';
        const path = `lexicon/entries/search${strictPath}/${languageEncoded}/${stemEncoded}`;

        return Server.newRequest().get<RawLexiconEntryResult[]>(path).then(result => {
            const matches: LexiconEntryMatch[] = [];
            result.forEach(match => {
                const lexicon = this.lexicons.getLexicon(match.id);
                matches.push(...match.standard.map(standard => new LexiconEntryMatch({
                    isSimpleMatch: false,
                    language,
                    lexicon,
                    number: standard.number,
                    text: standard.text
                })));
                matches.push(...match.simple.map(simple => new LexiconEntryMatch({
                    isSimpleMatch: true,
                    language,
                    lexicon,
                    number: simple.number,
                    text: simple.text
                })));
            });
            return new LexiconEntryResult(matches);
        });
    }
}
